import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Stepper from 'components/base/stepper';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import useGetStartedActionsContext from 'contexts/get-started-actions.context';
import { usePaystackPayment } from 'react-paystack';
import { BrandType } from 'models/brand.model';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { handleApiError } from 'utils/functions';
import { DepartmentType } from 'models/department.model';
import { MemberListType } from 'models/member.model';
import { ENUM_ONBOARDING_STATUS, ENUM_ONBOARDING_STEP } from 'utils/enum';

export const teamDefaultData = {
  first_name: '',
  last_name: '',
  phone_number: '',
  email: '',
  birthday: '',
  department: '',
  budget: '',
  budget_duration: '',
  role: 'MEMBER',
  department_id: ''
};

const AddYourTeamLayout = () => {
  const BANI_PUBLIC_KEY = process.env.REACT_APP_BANI_PUBLIC_KEY;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    paymentAmount,
    setPaymentAmount,
    setActivePaymentCard,
    setPaymentType,
    paymentType,
    paymentView,
    setPaymentView,
    activePaymentCard,
    paymentLoading,
    setPaymentLoading,
    setHideSidebar,
    setBackLinkTitle,
    setIsBackLink,
    setBackLink,
    activeCompany,
    paystackProps,
    refetchActiveCompany,
    refetchCompanyWalletDetails,
    refetchOnboarding
  } = useDashboardContext();

  const {
    isDisabled,
    setActive,
    departmentFormData,
    setDepartmentFormData,
    teamDataForm,
    setTeamDataForm,
    brandFormData,
    addressesFormData,
    setBrandFormData,
    operatingDays,
    workModes
  } = useGetStartedActionsContext();
  const [departmentList, setDepartmentList] = useState<DepartmentType[]>([]);
  const [teamList, setTeamList] = useState<MemberListType[]>([]);
  const [brand, setBrand] = useState<null | BrandType>(null);
  const [coordinates, setCoordinates] = useState({
    lat: '',
    lng: ''
  });
  const [loading, setLoading] = useState(false);

  const navUrl = [
    { name: 'Add branch addresses', url: ROUTE_KEYS.ADD_BRANCH_ADDRESSES },
    { name: 'Set office operating days', url: ROUTE_KEYS.SET_OPERATING_DAYS },
    { name: 'Add department', url: ROUTE_KEYS.ADD_DEPARTMENT },
    { name: 'Add your team', url: ROUTE_KEYS.ADD_YOUR_TEAM },
    { name: 'Top-up wallet', url: ROUTE_KEYS.TOP_UP_WALLET, canSkip: true }
  ];

  const companyId = activeCompany.id;

  const isTopup = () => pathname.includes(ROUTE_KEYS.GET_STARTED_ACTION);

  const initializePayment: any = usePaystackPayment({
    ...paystackProps,
    metadata: {
      ...paystackProps.metadata,
      customer: {
        ...paystackProps.metadata.customer,
        fund_business_wallet: true
      }
    }
  } as any);

  const addDepartment = async () => {
    if (!companyId) return;
    const filteredData = departmentFormData?.filter((item) => item.name !== '');
    const data = filteredData.map((item) => {
      return {
        name: item.name,
        company_id: companyId
      };
    });
    try {
      setLoading(true);
      const res = await apiService.post(`department/add/multiple`, { data });
      if (res) {
        await refetchActiveCompany();
        toast(<ToastMessage type='success' message='Department added successfully' />);
      }
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addTeam = async () => {
    if (!companyId) return;
    const members_data = teamDataForm?.map((item) => {
      return {
        phone_number: formatPhoneNumber(item.phone_number ?? ''),
        first_name: item.first_name,
        last_name: item.last_name,
        dob: item.dob,
        email: item.email,
        company_id: companyId,
        department_name: item.department,
        role: item.role === 'ADMIN' ? 'ADMIN' : 'MEMBER',
        budget: item.budget,
        budget_duration: item.budget_duration
      };
    });
    const data = { company_id: companyId, members_data };
    try {
      setLoading(true);
      const res = await apiService.post('member/bulk', { data });
      if (res) {
        await refetchActiveCompany();
        toast(<ToastMessage type='success' message='Team added successfully' />);
      }
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addBranchAddresses = async () => {
    if (!companyId || addressesFormData.some((data) => !data.address)) return;
    try {
      setLoading(true);
      const data = {
        data: await Promise.all(
          addressesFormData.map(async (data) => {
            const geocodes = await geocodeByAddress(data.address);
            const { lat: latitude, lng: longitude } = await getLatLng(geocodes[0]);
            return { ...data, latitude, longitude };
          })
        )
      };
      await apiService.post(`branches/bulk/${companyId}`, { data });
      await refetchActiveCompany();
      toast(<ToastMessage type='success' message='Branch addresses added successfully' />);
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createOperatingDays = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const data = {
        ...operatingDays,
        days: operatingDays.days.map((day) => ({ ...day, is_active: String(day.is_active) }))
      };
      await apiService.put(`company/${companyId}/days`, { data });
      await refetchActiveCompany();
      toast(<ToastMessage type='success' message='Operating days set successfully' />);
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const completePaymentStep = async () => {
    if (!companyId) return;
    await apiService.put(`company/${companyId}/onboarding`, {
      data: { step: ENUM_ONBOARDING_STEP.TOP_UP_WALLET, status: ENUM_ONBOARDING_STATUS.COMPLETED }
    });
    await refetchOnboarding();
  };
  const uploadWorkModes = async () => {
    await apiService.put('company/update', {
      data: { work_modes: workModes, company_id: companyId }
    });
  };

  const handleOnClick = async () => {
    if (pathname.includes(navUrl[0].url)) return await addBranchAddresses();
    else if (pathname.includes(navUrl[1].url)) {
      await createOperatingDays();
      await uploadWorkModes();
    } else if (pathname.includes(navUrl[2].url)) return await addDepartment();
    else if (pathname.includes(navUrl[3].url)) return await addTeam();
  };

  const handleOnSuccess = async () => {
    await refetchActiveCompany();
    await refetchCompanyWalletDetails();
    completePaymentStep();
    toast(<ToastMessage type='success' message='Payment successful, processing wallet balance' />);
    navigate('/get-started?assign_admin=true');
  };

  const handleBaniTopup = () => {
    (window as any).BaniPopUp({
      amount: paymentAmount,
      phoneNumber: formatPhoneNumber(activeCompany.user.phone_number),
      merchantKey: BANI_PUBLIC_KEY,
      email: activeCompany.user.email,
      firstName: activeCompany.user.first_name,
      lastName: activeCompany.user.last_name,
      bankTransfer: true,
      metadata: {
        customer: {
          webhook: true,
          fund_business_wallet: true,
          phone_number: formatPhoneNumber(activeCompany?.user?.phone_number),
          body: { company_id: activeCompany.id }
        }
      },
      callback: handleOnSuccess
    });
  };

  async function handlePayment() {
    if (isDisabled) {
      await apiService.put(`company/${activeCompany.id}/onboarding`, {
        data: { step: ENUM_ONBOARDING_STEP.TOP_UP_WALLET, status: ENUM_ONBOARDING_STATUS.SKIPPED }
      });
      refetchActiveCompany();
      return navigate('/get-started?assign_admin=true');
    }
    if (paymentType === 'transfer' && paymentView === 'methods') return setPaymentView('pay');
    if (paymentType === 'transfer') {
      handleBaniTopup();
      return;
    }
    if (paymentType === 'paystack' && paymentView === 'methods') return setPaymentView('pay');
    if (paymentType === 'paystack' && activePaymentCard && paymentView === 'methods')
      return setPaymentView('pay');
    setPaymentLoading(true);
    if (paymentType === 'paystack' && !activePaymentCard)
      return initializePayment(
        (context: any) => {
          if (context.status === 'success') {
            toast(
              <ToastMessage
                type='success'
                message='Payment successful, processing wallet balance'
              />
            );
            completePaymentStep();
            navigate('/get-started?assign_admin=true');
          } else toast(<ToastMessage type='error' message='Payment failed' />);
          setPaymentLoading(false);
        },
        () => {
          setPaymentLoading(false);
        }
      );
    if (!companyId) return;
    try {
      await apiService.post('card/charge/', {
        data: {
          card_id: activePaymentCard?.id,
          amount: paymentAmount,
          company_id: companyId
        }
      });
      navigate('/get-started?assign_admin=true');
      toast(
        <ToastMessage type='success' message='Payment successful, processing wallet balance' />
      );
    } catch (err) {
      handleApiError(err, 'Payment failed');
    } finally {
      setPaymentLoading(false);
    }
  }

  useEffect(() => {
    if (isTopup()) {
      setHideSidebar(true);
      setIsBackLink(true);
      setBackLink(ROUTE_KEYS.GET_STARTED);
      setBackLinkTitle('Return');
    }
    setPaymentView('methods');
    setPaymentAmount('');
    setActivePaymentCard(null);
    setPaymentType(null);
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, [pathname]);

  const fetchTeams = async () => {
    if (!companyId) return;
    try {
      const res = await apiService.get('member', { params: { company_id: companyId } });
      if (res) {
        setTeamList(res?.data?.data?.results);
        if (res?.data?.data?.results?.length > 0) {
          setActive('manual');
        }
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const fetchBrand = async () => {
    try {
      const res = await apiService.get('company/get-brand');
      setBrand(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchTeams();
      fetchDepartment();
      fetchBrand();
    }
  }, [companyId]);

  const fetchDepartment = async () => {
    if (!companyId) return;
    try {
      const res = await apiService.get(`department/company/${companyId}`);
      setDepartmentList(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const getCokitchenId = async () => {
    try {
      const res = await apiService.post(`internal/location`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        isInternal: true,
        data: {
          lat: coordinates?.lat,
          lng: coordinates?.lng
        }
      });
      setBrandFormData({ ...brandFormData, cokitchen_id: res.data.cokitchen.id });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (departmentList?.length) {
      setDepartmentFormData(departmentList);
    } else {
      setDepartmentFormData([{ name: '' }]);
    }
  }, [departmentList]);

  useEffect(() => {
    if (teamList?.length) {
      const userArray = teamList.map((item: MemberListType) => ({
        ...item.user,
        department: item.department.name,
        budget: item.budget
      }));
      setTeamDataForm(userArray);
    } else {
      setTeamDataForm([teamDefaultData]);
    }
  }, [teamList]);

  useEffect(() => {
    if (brand) {
      setBrandFormData({
        name: brand.name,
        images: brand.images,
        cokitchen_id: brand.cokitchen_id
      });
    } else {
      getCokitchenId();
    }
  }, [brand]);

  useEffect(() => {
    if (navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoordinates({
            lat: position.coords.latitude.toString(),
            lng: position.coords.longitude.toString()
          });
        },
        (error) => {
          if (error.PERMISSION_DENIED) {
            console.log(error);
          }
        }
      );
    }
  }, []);

  return (
    <div className='flex justify-center'>
      <Stepper
        loading={loading || paymentLoading}
        onConfirm={handlePayment}
        pages={navUrl}
        isDisabled={isDisabled}
        onClick={handleOnClick}
        disableSideNav>
        <Outlet />
      </Stepper>
    </div>
  );
};

export default AddYourTeamLayout;
